import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Check,
  ChevronRight,
  Star,
  Sparkles,
  Shield,
  Zap,
  ArrowRight,
  Loader,
} from "lucide-react";
import { auth } from "../firebase";
import { getUserSubscription } from "../services/subscriptionService";

const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [verifying, setVerifying] = useState(true);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const sessionId = searchParams.get("session_id");

  // Verify subscription status
  useEffect(() => {
    const verifySubscription = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate("/login");
          return;
        }

        // Wait a moment for webhook to process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Check subscription status
        const status = await getUserSubscription(user.uid);
        if (status === "active" || status === "past_due") {
          setSubscriptionActive(true);
        } else {
          // Subscription might still be processing, wait a bit more
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const retryStatus = await getUserSubscription(user.uid);
          setSubscriptionActive(
            retryStatus === "active" || retryStatus === "past_due"
          );
        }
      } catch (error) {
        console.error("Error verifying subscription:", error);
        // Continue anyway - webhook will update it eventually
        setSubscriptionActive(true);
      } finally {
        setVerifying(false);
      }
    };

    verifySubscription();
  }, [navigate]);

  // Auto-redirect after countdown
  useEffect(() => {
    if (!verifying && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate("/home");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [navigate, verifying, countdown]);

  const handleContinue = () => {
    navigate("/home");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col items-center justify-center p-4">
      {/* Branding Header */}
      <div className="absolute top-6 left-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 blur opacity-70 animate-pulse"></div>
            <img
              src="/QemplateFull.png"
              alt="Qemplate Logo"
              className="relative h-10 w-auto z-10"
            />
          </div>
        </div>
      </div>

      {/* Success Card */}
      <div className="max-w-2xl w-full">
        {/* Main Success Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-8 text-white relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            ></div>
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-6 animate-bounce">
                <Check className="h-12 w-12 text-white" strokeWidth={3} />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                Payment successful
              </h1>
              <p className="text-lg text-indigo-100">
                Your subscription is now active.
              </p>
              {sessionId && (
                <p className="text-sm text-indigo-200 mt-2 opacity-75">
                  Session ID: {sessionId.substring(0, 20)}...
                </p>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Success Message */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-6 w-6 text-yellow-500 ${
                      i === 2 ? "h-8 w-8" : ""
                    }`}
                    fill="currentColor"
                  />
                ))}
              </div>
              {verifying ? (
                <>
                  <div className="flex items-center justify-center mb-4">
                    <Loader className="h-8 w-8 text-indigo-600 animate-spin" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Verifying Your Subscription...
                  </h2>
                  <p className="text-gray-600">
                    Please wait while we confirm your payment and activate your
                    Pro features.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Subscription activated
                  </h2>
                  <p className="text-gray-600">
                    You now have access to the features included in your plan.
                  </p>
                </>
              )}
            </div>

            {/* Features Unlocked */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-indigo-100 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Features included
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-indigo-600" />
                      <span>12 Quiz Slots</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-indigo-600" />
                      <span>25 AI Requests/day</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-indigo-600" />
                      <span>Full Vault Access</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-indigo-600" />
                      <span>Advanced Analytics</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleContinue}
              disabled={verifying}
              className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-semibold transition-all duration-200 ${
                verifying
                  ? "bg-gray-300 cursor-not-allowed text-gray-500"
                  : "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white hover:shadow-2xl hover:shadow-purple-500/50 transform hover:-translate-y-0.5"
              }`}
            >
              {verifying ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            {!verifying && (
              <p className="text-center text-gray-500 text-sm mt-4">
                Redirecting to home in {countdown} seconds...
              </p>
            )}
          </div>
        </div>

        {/* Order Summary Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" />
            Order Summary
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <span className="text-gray-600">Pro Subscription</span>
              <span className="font-semibold text-gray-900">£4.99/month</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <span className="text-gray-600">Billing Cycle</span>
              <span className="text-gray-900">Monthly</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-gray-600">Next Billing Date</span>
              <span className="text-gray-900">
                {new Date(
                  Date.now() + 30 * 24 * 60 * 60 * 1000
                ).toLocaleDateString()}
              </span>
            </div>
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Receipt</span>
                <span className="text-indigo-600 font-medium cursor-pointer hover:underline">
                  View in Stripe Dashboard
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
